import { useEffect, useState, ChangeEvent, useCallback } from 'react';
import { Formik, Form, Field } from 'formik';
import { Col, Row } from 'react-bootstrap';
import * as yup from 'yup';
import { DefaultTheme, useTheme } from 'styled-components';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import { useHistory, useParams } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { Input } from '../../../components/Input';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { ReactSelect } from '../../../components/ReactSelect';
import api from '../../../services/api';
import { ISelect } from '../../../interfaces/ISelect';
import { Button } from '../../../components/Button';
import { IFileImage } from '../../../interfaces/IFileImage';
import { UploadThumbnail } from '../../../components/UploadThumbnail';
import { ICampaing } from '../../../interfaces/ICampaing';

const dataInitial = {
  nameCampaign: '',
  keyword: '',
  clientId: '',
  finalDate: '',
  startDate: '',
  influencers: [],
  socialNetworks: [],
};

const schema = yup.object({
  nameCampaign: yup.string().required('Digite o nome'),
  keyword: yup.string().required('Digite a palavra-chave'),
  clientId: yup.lazy(value => {
    if (typeof value === 'object') {
      return yup.object().nullable().required('Campo obrigatório');
    }
    return yup.string().required('Campo obrigatório');
  }),
  finalDate: yup.string().required('Campo obrigatório'),
  startDate: yup.string().required('Campo obrigatório'),
  influencers: yup
    .array()
    .required('Campo obrigatório')
    .min(1, 'Selecione no mínimo 1'),
  socialNetworks: yup
    .array()
    .required('Campo obrigatório')
    .min(1, 'Selecione no mínimo 1'),
});

interface SubmitProps {
  nameCampaign: string;
  keyword: string;
  clientId: any;
  finalDate: string;
  startDate: string;
  influencers: ISelect[];
  socialNetworks: ISelect[];
}

export const FormCampaign = () => {
  const theme: DefaultTheme = useTheme();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [clients, setClients] = useState<ISelect[]>([]);
  const [influencersPreload, setInfluencersPreload] = useState<ISelect[]>([]);
  const [socialNetworksPreload, setSocialNetworksPreload] = useState<ISelect[]>(
    [],
  );
  const [campaign, setCampaing] = useState<ICampaing>();
  const [loading, setLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState<IFileImage>();

  const handleSubmit = async ({
    clientId,
    finalDate,
    startDate,
    influencers,
    nameCampaign,
    keyword,
    socialNetworks,
  }: SubmitProps) => {
    try {
      setLoading(true);
      if (!thumbnail && !id) {
        toast.error('Adicione a logo');
        return;
      }
      const influencersArray = influencers.map(p => p.value).concat([]);
      const socialNetworksArray = socialNetworks.map(p => p.value).concat([]);
      const dataFormatted = {
        nameCampaign,
        keyword,
        clientId: clientId.value || clientId,
        startDate,
        finalDate,
        influencers: JSON.stringify(influencersArray),
        socialNetworks: JSON.stringify(socialNetworksArray),
        file: thumbnail?.file,
      };
      const formData = serialize(dataFormatted);
      if (id) {
        await api.put(`/campaigns/${id}`, formData);
      } else {
        await api.post('/campaigns', formData);
      }
      toast.success('Campanha salva com sucesso');
      history.push('/campaigns');
    } catch (err: any) {
      if (err.response.data.message) {
        toast.error(err.response.data.message);
        return;
      }
      toast.error('Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get('/clients?isAll=true')
      .then(({ data }) =>
        setClients(
          data.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        ),
      )
      .catch(() => toast.error('Erro ao buscar clients'));

    api
      .get('/influencers?isAll=true')
      .then(({ data }) =>
        setInfluencersPreload(
          data.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        ),
      )
      .catch(() => toast.error('Erro ao buscar influenciadores'));

    api
      .get('/socialNetworks')
      .then(({ data }) =>
        setSocialNetworksPreload(
          data.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        ),
      )
      .catch(() => toast.error('Erro ao buscar redes sociais'));
  }, []);

  useEffect(() => {
    if (id) {
      api
        .get(`/campaigns/${id}`)
        .then(({ data }) => {
          const dataFormatted = {
            ...data,
            socialNetworks: data.socialNetworks
              .map((p: any) => ({
                label: p.name,
                value: p.id,
              }))
              .concat([]),
            influencers: data.influencers
              .map((p: any) => ({
                label: p.name,
                value: p.id,
              }))
              .concat([]),
            startDate: format(parseISO(data.startDate), 'yyyy-MM-dd'),
            finalDate: format(parseISO(data.finalDate), 'yyyy-MM-dd'),
          };
          setCampaing(dataFormatted);
        })
        .catch(() => toast.error('Erro ao buscar campanha'));
    }
  }, [id]);

  const onSelectFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const url = URL.createObjectURL(e.target.files[0]);
      setThumbnail({ file: e.target.files[0], url });
    }
  }, []);

  return (
    <div>
      <Breadcrumb
        items={[
          {
            title: 'Campanhas',
            link: '/campaigns',
          },
          {
            title: 'Cadastro de campanha',
          },
        ]}
      />
      <Row className="d-flex align-items-center mt-4">
        <Col md={10} xs={9}>
          <h3>Nova campanha</h3>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={{ span: 6, offset: 3 }}>
          <Formik
            initialValues={campaign || dataInitial}
            enableReinitialize
            onSubmit={handleSubmit}
            validationSchema={schema}
          >
            <Form>
              <UploadThumbnail
                onSubmit={onSelectFile}
                urlThumbnail={thumbnail?.url || campaign?.logo}
              />
              <Input
                type="text"
                name="nameCampaign"
                placeholder="Nome da campanha"
              />
              <Input
                type="text"
                name="keyword"
                placeholder="Palavra-chave"
              />
              <Field
                name="clientId"
                options={clients}
                placeholder="Cliente"
                component={ReactSelect}
              />
              <Row>
                <Col xs={6}>
                  <label className="labelData">
                    <p>Data início</p>
                    <Input
                      type="date"
                      name="startDate"
                      placeholder="Data de inicio"
                    />
                  </label>
                </Col>
                <Col xs={6}>
                  <label className="labelData">
                    <p>Data fim</p>
                    <Input
                      type="date"
                      name="finalDate"
                      placeholder="Data de inicio"
                    />
                  </label>
                </Col>
              </Row>

              <Field
                name="influencers"
                options={influencersPreload}
                placeholder="Influenciadores"
                component={ReactSelect}
                isMulti
              />

              <Field
                name="socialNetworks"
                options={socialNetworksPreload}
                component={ReactSelect}
                isMulti
                placeholder="Redes sociais"
              />

              <Col md={{ span: 6, offset: 3 }}>
                <Button
                  className="mt-4"
                  type="submit"
                  background={theme.colors.secondary}
                  loading={loading}
                  color="#fff"
                >
                  Salvar
                </Button>
              </Col>
            </Form>
          </Formik>
        </Col>
      </Row>
    </div>
  );
};
